// Helpers for translating between Quill's serialized HTML (which always wraps
// each line in a <p>...</p> block) and the backend's storage format (which
// omits the outermost <p> wrapper for single-paragraph content).

// Removes the outermost <p>...</p> only when the HTML is a single top-level
// paragraph. Multi-paragraph content (e.g. <p>a</p><p>b</p>) is returned
// unchanged because there is no single outer wrapper to strip.
export const stripOuterP = html => {
  if (!html) return html;
  const match = html.match(/^<p>([\s\S]*)<\/p>$/);
  if (!match) return html;
  const inner = match[1];
  if (/<\/?p\b/i.test(inner)) return html;
  return inner;
};

// Wraps content in <p>...</p> unless it already begins with a <p> tag.
// Idempotent so legacy backend values that still carry the wrapper round-trip
// through Quill cleanly without becoming <p><p>...</p></p>.
export const wrapWithOuterP = html => {
  if (!html) return html;
  if (/^<p[\s>]/i.test(html)) return html;
  return `<p>${html}</p>`;
};

// The only tags we keep when sanitizing Quill output for the backend.
const ALLOWED_TAGS = new Set(["SUP", "SUB"]);

// The character &nbsp; decodes to (U+00A0). Normalized to a plain space, a
// common Word-paste artifact.
const NBSP = String.fromCharCode(160);

// Produces the human-readable form of a decoded text node. Entities have
// already been decoded by DOMParser (e.g. &lt; -> <, &amp; -> &), and we keep
// them decoded so the value submitted to the backend is human-readable rather
// than HTML-escaped. Only the non-breaking space is normalized to a plain one.
const normalizeText = text => text.split(NBSP).join(" ");

// Sanitizes Quill's serialized HTML before it is stored/submitted to the
// backend. Users often paste from Word, which drags in spans, styles, classes
// and other markup on top of the <sup>/<sub> we actually support. This:
//   - drops every tag except <sup> and <sub> (their text content is kept),
//   - strips all attributes from the surviving <sup>/<sub> tags,
//   - decodes HTML entities to human-readable characters (e.g. &nbsp; -> space,
//     &lt; -> <, &gt; -> >, &amp; -> &, &eacute; -> the accented letter).
export const sanitizeQuillHtml = html => {
  if (!html) return html;

  // Guard for non-browser environments (e.g. SSR); leave the value untouched.
  if (typeof DOMParser === "undefined") return html;

  const doc = new DOMParser().parseFromString(html, "text/html");

  const TEXT_NODE = 3;
  const ELEMENT_NODE = 1;

  const serialize = node => {
    let out = "";
    node.childNodes.forEach(child => {
      if (child.nodeType === TEXT_NODE) {
        out += normalizeText(child.nodeValue || "");
      } else if (child.nodeType === ELEMENT_NODE) {
        const tag = child.tagName;
        if (ALLOWED_TAGS.has(tag)) {
          // Rebuild the tag with no attributes, keeping sanitized children.
          const name = tag.toLowerCase();
          out += `<${name}>${serialize(child)}</${name}>`;
        } else {
          // Unwrap any other tag, preserving its (sanitized) content.
          out += serialize(child);
        }
      }
    });
    return out;
  };

  return serialize(doc.body);
};
